import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, ArrowRight, Clock, ShieldCheck } from 'lucide-react';
import './Signup.css';
import './SignupCheckEmail.css';

/* Terminal screen of the signup flow.

   Signup intentionally ends signed OUT. The account exists and is paid for, but
   its password is a system-generated temporary one that was emailed in
   plaintext, so the user has to come back through /login with it. That round
   trip is the point: it proves they can actually read the mailbox they signed
   up with before the account becomes usable.

   This screen therefore has exactly one job: tell them where the password went
   and send them to the login page. No session, no dashboard preview, no
   "continue anyway" escape. */

const SignupCheckEmail = () => {
  const navigate = useNavigate();

  // The email is passed via navigation state or query string so the screen can
  // name the exact mailbox. It is presentational only, nothing here is
  // authenticated, so an absent or tampered value costs nothing.
  const [email, setEmail] = useState('');

  useEffect(() => {
    document.body.classList.add('nx-onboard-body');
    return () => document.body.classList.remove('nx-onboard-body');
  }, []);

  useEffect(() => {
    const fromQuery = new URLSearchParams(window.location.search).get('email');
    const fromState = window.history.state?.usr?.email;
    setEmail(fromQuery || fromState || '');
  }, []);

  return (
    <div className="nx-onboard-page nx-checkemail-page">
      <div className="nx-topbar">
        <div className="nx-brand">
          <div className="nx-brand-mark">
            <img className="nx-brand-logo" src="/images/NexOpta_Icon.png" alt="Nexopta" />
          </div>
          <div className="nx-brand-word">NEXOPTA</div>
        </div>
        <div className="nx-step">Setup complete</div>
      </div>

      <div className="nx-progress-wrap">
        <div className="nx-progress-track">
          <div className="nx-progress-fill" style={{ width: '100%' }} />
        </div>
      </div>

      <div className="nx-checkemail-content">
        <div className="nx-checkemail-card">
          <div className="nx-checkemail-icon" aria-hidden="true">
            <Mail size={26} />
          </div>

          <h1 className="nx-checkemail-title">Check your email</h1>

          <p className="nx-checkemail-lead">
            Your account is ready. We've sent a temporary password to
            {email ? <> <strong>{email}</strong></> : ' the email address you signed up with'}.
            Use it to sign in, and you'll be asked to choose your own password
            straight away.
          </p>

          <ol className="nx-checkemail-steps">
            <li>
              <span className="nx-checkemail-step-num">1</span>
              <span>Open the email from us and copy the temporary password.</span>
            </li>
            <li>
              <span className="nx-checkemail-step-num">2</span>
              <span>Sign in with your email address and that password.</span>
            </li>
            <li>
              <span className="nx-checkemail-step-num">3</span>
              <span>Set a password of your own, then you're in.</span>
            </li>
          </ol>

          <button
            type="button"
            className="nx-primary-btn nx-checkemail-cta"
            onClick={() => navigate('/login', { replace: true })}
          >
            Go to sign in
            <ArrowRight size={18} />
          </button>

          <div className="nx-checkemail-notes">
            <p className="nx-checkemail-note">
              <ShieldCheck size={15} aria-hidden="true" />
              <span>
                The temporary password only works until you replace it, so it
                can't be reused later.
              </span>
            </p>
            <p className="nx-checkemail-note">
              <Clock size={15} aria-hidden="true" />
              <span>
                Nothing yet? Give it a minute, then check your spam or junk
                folder.
              </span>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignupCheckEmail;
